import { api } from '../api'
import type {
  ActivateCampaignResult,
  CampaignResult,
  CampaignStats,
  CampaignStatus,
  CreateCampaignBody,
  DefineSequenceBody,
  DefineSequenceResult,
  DeleteCampaignResult,
  GetCampaignResult,
  ListCampaignsResult,
  PauseCampaignResult,
  RecentSends,
  UpcomingSends,
  UpdateCampaignSettingsBody,
  UpdateCampaignSettingsResult,
} from '../types'
import type { components } from '../schema'

type S = components['schemas']

// --- Email (Smartlead) shapes this file's endpoints carry (BIBLE §14). Named here rather
// than in `../types` so the email campaign screens can say them without that file moving.
export type EmailCampaignSettings = S['EmailCampaignSettings']
export type StopLeadOn = S['StopLeadOn']
export type TrackingOptOut = S['TrackingOptOut']
export type StepVariant = S['StepVariant']
export type EmailCampaignFunnel = S['EmailCampaignFunnel']
export type EmailEngagement = S['EmailEngagement']
export type RecentEmailActivity = S['RecentEmailActivity']
export type EmailStatistic = S['EmailStatistic']
export type DailyEngagement = S['DailyEngagement']
export type CampaignAnalytics = S['GetCampaignAnalyticsResult']
export type CampaignEmailDays = S['GetCampaignEmailDaysResult']
export type SyncEmailDaysResult = S['SyncEmailDailyStatsResult']

export interface CampaignAnalyticsArgs {
  id: string
  /** ISO dates (`YYYY-MM-DD`), both inclusive. */
  start: string
  end: string
}

/** Arming, pausing and importing all move the same three things. Named once. */
const CAMPAIGN_MOVED = (id: string) =>
  [
    { type: 'Campaign' as const, id },
    { type: 'Campaign' as const, id: 'LIST' },
    { type: 'CampaignStats' as const, id },
    { type: 'Schedule' as const, id },
    { type: 'Activity' as const, id },
    { type: 'Lead' as const, id },
    { type: 'CampaignAnalytics' as const, id },
  ] as const

export const campaignsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listCampaigns: build.query<
      ListCampaignsResult,
      { status?: CampaignStatus; clientId?: string | null } | void
    >({
      // `clientId` is the switcher's client (BIBLE §19); absent or null is every client's.
      query: (args) => ({
        url: '/campaigns',
        params: {
          ...(args?.status ? { status: args.status } : {}),
          ...(args?.clientId ? { client_id: args.clientId } : {}),
        },
      }),
      providesTags: (result) => [
        { type: 'Campaign', id: 'LIST' },
        ...(result?.items ?? []).map((campaign) => ({ type: 'Campaign' as const, id: campaign.id })),
      ],
    }),

    getCampaign: build.query<GetCampaignResult, string>({
      query: (id) => `/campaigns/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Campaign', id }],
    }),

    getCampaignStats: build.query<CampaignStats, string>({
      query: (id) => `/campaigns/${id}/stats`,
      providesTags: (_result, _error, id) => [{ type: 'CampaignStats', id }],
    }),

    getCampaignSchedule: build.query<UpcomingSends, { id: string; limit?: number }>({
      query: ({ id, limit }) => ({
        url: `/campaigns/${id}/schedule`,
        params: limit ? { limit } : {},
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Schedule', id }],
    }),

    getCampaignActivity: build.query<RecentSends, { id: string; limit?: number }>({
      // The other half of the schedule: what already went out, and how it went. Polled
      // like the schedule is, because "did that invite land?" is a question asked while
      // watching rather than on a reload.
      query: ({ id, limit }) => ({
        url: `/campaigns/${id}/activity`,
        params: limit ? { limit } : {},
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Activity', id }],
    }),

    getCampaignAnalytics: build.query<CampaignAnalytics, CampaignAnalyticsArgs>({
      // An email campaign's day-by-day engagement. The backend splits a long range into
      // the 30-day spans Smartlead will answer, so a year is one request from here.
      query: ({ id, start, end }) => ({ url: `/campaigns/${id}/analytics`, params: { start, end } }),
      providesTags: (_result, _error, { id }) => [{ type: 'CampaignAnalytics', id }],
    }),

    getCampaignEmailDays: build.query<CampaignEmailDays, { id: string; days: number }>({
      // Our stored copy of Smartlead's by-day numbers: no provider call (BIBLE §14).
      query: ({ id, days }) => ({ url: `/campaigns/${id}/email-days`, params: { days } }),
      providesTags: (_result, _error, { id }) => [{ type: 'EmailDays', id }],
    }),

    // "Sync now": the backend reads today, yesterday and missing days from Smartlead.
    syncCampaignEmailDays: build.mutation<SyncEmailDaysResult, string>({
      query: (id) => ({ url: `/campaigns/${id}/email-days/sync`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'EmailDays', id },
        { type: 'EmailDays', id: 'ALL' },
        { type: 'CampaignStats', id },
      ],
    }),

    createCampaign: build.mutation<CampaignResult, CreateCampaignBody>({
      query: (body) => ({ url: '/campaigns', method: 'POST', body }),
      invalidatesTags: [{ type: 'Campaign', id: 'LIST' }],
    }),

    updateCampaignSettings: build.mutation<
      UpdateCampaignSettingsResult,
      UpdateCampaignSettingsBody
    >({
      // A window edit re-snaps pending sends inside the service (BIBLE §4.9), so the
      // schedule this campaign shows is stale the moment this returns.
      query: (body) => ({ url: '/campaigns/settings', method: 'POST', body }),
      invalidatesTags: (_result, _error, body) => CAMPAIGN_MOVED(body.campaign_id),
    }),

    defineSequence: build.mutation<DefineSequenceResult, DefineSequenceBody>({
      query: (body) => ({ url: '/campaigns/sequence', method: 'POST', body }),
      invalidatesTags: (_result, _error, body) => [{ type: 'Campaign', id: body.campaign_id }],
    }),

    activateCampaign: build.mutation<ActivateCampaignResult, string>({
      query: (campaign_id) => ({
        url: '/campaigns/activate',
        method: 'POST',
        body: { campaign_id },
      }),
      invalidatesTags: (_result, _error, id) => CAMPAIGN_MOVED(id),
    }),

    pauseCampaign: build.mutation<PauseCampaignResult, string>({
      query: (campaign_id) => ({ url: '/campaigns/pause', method: 'POST', body: { campaign_id } }),
      invalidatesTags: (_result, _error, id) => CAMPAIGN_MOVED(id),
    }),

    // A delete can leave the campaign behind as a hidden row when somebody replied to it,
    // so the list is invalidated the same way a move is: whether the row went or merely
    // stopped being listed, what the operator sees next has to be re-read.
    deleteCampaign: build.mutation<DeleteCampaignResult, string>({
      query: (id) => ({ url: `/campaigns/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => CAMPAIGN_MOVED(id),
    }),
  }),
})

export const {
  useListCampaignsQuery,
  useGetCampaignQuery,
  useGetCampaignStatsQuery,
  useGetCampaignScheduleQuery,
  useGetCampaignActivityQuery,
  useGetCampaignAnalyticsQuery,
  useGetCampaignEmailDaysQuery,
  useSyncCampaignEmailDaysMutation,
  useCreateCampaignMutation,
  useUpdateCampaignSettingsMutation,
  useDefineSequenceMutation,
  useActivateCampaignMutation,
  usePauseCampaignMutation,
  useDeleteCampaignMutation,
} = campaignsApi

/**
 * Smartlead's CSV of an email campaign's leads, for `downloadFile` — a file, not JSON, so
 * it is fetched outside RTK Query (see `features/prospecting/download.ts`).
 */
export function campaignLeadsExportPath(id: string): string {
  return `campaigns/${id}/leads-export`
}
